import React from 'react';
import { motion } from 'framer-motion';

/**
 * AtomScene — Pure SVG + CSS centerpiece for hero.
 * Three intersecting orbital rings + glowing core + orbiting electrons.
 * Renders perfectly in static SEO snapshots (no WebGL needed).
 */
const AtomScene = () => {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      aria-hidden="true"
    >
      <div className="relative w-[700px] h-[700px] max-w-[90vw] max-h-[90vw]">
        {/* Outer pulse rings */}
        <motion.div
          className="absolute inset-0 rounded-full border border-[#FF6B00]/20"
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute inset-0 rounded-full border border-[#FF6B00]/15"
          animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeOut', delay: 1.2 }}
        />

        {/* Glow halo behind core */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(255,107,0,0.45) 0%, rgba(255,107,0,0.15) 30%, transparent 60%)',
            filter: 'blur(28px)',
          }}
        />

        {/* SVG orbital rings */}
        <svg
          viewBox="-200 -200 400 400"
          className="absolute inset-0 w-full h-full"
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="ringGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
              <stop offset="50%" stopColor="rgba(255,107,0,0.8)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.3)" />
            </linearGradient>
            <linearGradient id="ringGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,107,0,0.6)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.8)" />
              <stop offset="100%" stopColor="rgba(255,107,0,0.4)" />
            </linearGradient>
            <radialGradient id="coreGradient">
              <stop offset="0%" stopColor="#FFE4B5" />
              <stop offset="40%" stopColor="#FF6B00" />
              <stop offset="100%" stopColor="rgba(255,107,0,0)" />
            </radialGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Ring 1: Horizontal, rotating clockwise */}
          <motion.g
            animate={{ rotate: 360 }}
            transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: 'center' }}
          >
            <ellipse
              cx="0"
              cy="0"
              rx="180"
              ry="60"
              fill="none"
              stroke="url(#ringGradient1)"
              strokeWidth="1.2"
              opacity="0.85"
              filter="url(#glow)"
            />
            <circle cx="180" cy="0" r="6" fill="#FF6B00" filter="url(#glow)" />
          </motion.g>

          {/* Ring 2: Tilted ~60deg, rotating counter-clockwise */}
          <motion.g
            animate={{ rotate: -360 }}
            transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: 'center', transform: 'rotate(60deg)' }}
          >
            <g style={{ transform: 'rotate(60deg)' }}>
              <ellipse
                cx="0"
                cy="0"
                rx="180"
                ry="60"
                fill="none"
                stroke="url(#ringGradient2)"
                strokeWidth="1.2"
                opacity="0.85"
                filter="url(#glow)"
              />
              <circle cx="-180" cy="0" r="5" fill="#ffffff" filter="url(#glow)" />
            </g>
          </motion.g>

          {/* Ring 3: Tilted ~-60deg, rotating clockwise slower */}
          <motion.g
            animate={{ rotate: 360 }}
            transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: 'center' }}
          >
            <g style={{ transform: 'rotate(-60deg)' }}>
              <ellipse
                cx="0"
                cy="0"
                rx="180"
                ry="60"
                fill="none"
                stroke="url(#ringGradient1)"
                strokeWidth="1.2"
                opacity="0.7"
                filter="url(#glow)"
              />
              <circle cx="0" cy="60" r="5" fill="#FF6B00" filter="url(#glow)" />
            </g>
          </motion.g>

          {/* Pulsing core */}
          <motion.circle
            cx="0"
            cy="0"
            r="22"
            fill="url(#coreGradient)"
            filter="url(#glow)"
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: 'center' }}
          />
          <motion.circle
            cx="0"
            cy="0"
            r="9"
            fill="#FFE4B5"
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
      </div>
    </div>
  );
};

export default AtomScene;
